import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useMemo } from 'react';
import { useOptimisticMutation } from '@/lib/query/useOptimisticMutation';

export interface SystemNotification {
    id: string;
    type: string; // 'SYSTEM_ALERT' | 'SYSTEM_INFO' | 'SYSTEM_WARNING' | 'SYSTEM_SUCCESS' etc
    title: string;
    message: string;
    timestamp: Date;
    actionLink?: string;
    severity: 'high' | 'medium' | 'low';
    readAt?: string | null;
}

/**
 * Hook React `useSystemNotifications` que encapsula uma lógica reutilizável.
 * @returns {{ notifications: { id: string; type: string; title: string; message: string; timestamp: Date; actionLink: string | undefined; severity: "low" | "medium" | "high"; readAt: string | null | undefined; }[]; count: number; hasHighSeverity: boolean; markAsRead: UseMutateFunction<...>; markAllAsRead: UseMutateFunction<...>...} Retorna um valor do tipo `{ notifications: { id: string; type: string; title: string; message: string; timestamp: Date; actionLink: string | undefined; severity: "low" | "medium" | "high"; readAt: string | null | undefined; }[]; count: number; hasHighSeverity: boolean; markAsRead: UseMutateFunction<...>; markAllAsRead: UseMutateFunction<...>...`.
 */
export const useSystemNotifications = () => {
    const { user } = useAuth();

    const sb = supabase;

    const { data: notifications = [] } = useQuery({
        queryKey: ['system_notifications'],
        queryFn: async () => {
            if (!sb) return [];
            // Fetch System Notifications
            const { data, error } = await sb
                .from('system_notifications')
                .select('*')
                .order('created_at', { ascending: false })
                .limit(20);

            if (error) throw error;

            type NotificationRow = {
                id: string;
                type: string;
                title: string;
                message: string;
                created_at: string;
                link?: string;
                severity: string;
                read_at?: string | null;
            };

            return (data as NotificationRow[]).map(n => ({
                id: n.id,
                type: n.type,
                title: n.title,
                message: n.message,
                timestamp: new Date(n.created_at),
                actionLink: n.link,
                severity: n.severity as 'high' | 'medium' | 'low',
                readAt: n.read_at
            }));
        },
        enabled: !!user && !!sb,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

    // Derived state
    const unreadCount = useMemo(() =>
        notifications.filter(n => !n.readAt).length
        , [notifications]);

    const hasHighSeverity = useMemo(() =>
        notifications.some(n => n.severity === 'high' && !n.readAt)
        , [notifications]);

    // Mutations otimistas: UI atualiza instantâneo, rollback se servidor falhar.
    const markAsRead = useOptimisticMutation<string, SystemNotification[]>({
        queryKey: ['system_notifications'],
        mutationFn: async (id) => {
            if (!sb) throw new Error('Supabase não está configurado');
            const nowIso = new Date().toISOString();
            const { error } = await sb
                .from('system_notifications')
                .update({ read_at: nowIso })
                .eq('id', id);
            if (error) throw error;
        },
        applyOptimistic: (list, id) => {
            const nowIso = new Date().toISOString();
            return list?.map(n => n.id === id ? { ...n, readAt: nowIso } : n);
        },
    });

    const markAllAsRead = useOptimisticMutation<void, SystemNotification[]>({
        queryKey: ['system_notifications'],
        mutationFn: async () => {
            if (!sb) throw new Error('Supabase não está configurado');
            const nowIso = new Date().toISOString();
            const { error } = await sb
                .from('system_notifications')
                .update({ read_at: nowIso })
                .is('read_at', null);
            if (error) throw error;
        },
        applyOptimistic: (list) => {
            const nowIso = new Date().toISOString();
            return list?.map(n => n.readAt ? n : { ...n, readAt: nowIso });
        },
    });

    return {
        notifications,
        count: unreadCount,
        hasHighSeverity,
        markAsRead: markAsRead.mutate,
        markAllAsRead: markAllAsRead.mutate
    };
};
